import { leaveEditModeGuard } from "@components/HomePage/leaveEditModeGuard";
import ModalToOpenService from "@core-ui/ContextServices/ModalToOpenService/ModalToOpenService";
import ModalToOpen from "@core-ui/ContextServices/ModalToOpenService/model/ModalsToOpen";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import type SignOutGesCloud from "@ext/enterprise-cloud/components/SignInOut/GesCloudSignOutModal";
import { useGesCloudOrganizationStore } from "@ext/enterprise-cloud/ui-logic/stores/GesCloudOrganizationStore/GesCloudOrganizationStore.provider";
import t from "@ext/localization/locale/translate";
import { openAppSettings } from "@ext/settings/components/AppSettingsTrigger";
import { ThemeMenuItem } from "@ext/settings/components/ThemeMenuItem";
import { Level } from "@ext/settings/logic/settings";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	DropdownMenuTriggerButton,
} from "@ui-kit/Dropdown";
import { GlassToolbarToggleButton } from "@ui-kit/GlassToolbar";
import { Icon } from "@ui-kit/Icon";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import { type ComponentProps, useCallback, useState } from "react";
import HomeLayoutControls from "../../../../components/HomePage/HomeLayoutControls";
import {
	OpenOrganizationSettings,
	OpenSettings,
	SignOut,
	UserAvatar,
	type UserAvatarTriggerVariant,
} from "../../../../components/UserAvatar";
import type { OrganizationInfo } from "../../GesCloudApi";
import type GesCloudAppSettingsEditor from "../GesCloudAppSettingsEditor";

interface GesCloudSignInOutProps {
	showHomeLayoutControls?: boolean;
	showThemeToggle?: boolean;
	triggerVariant?: UserAvatarTriggerVariant;
}

export const GesCloudSignInOut = (props: GesCloudSignInOutProps) => {
	const isLogged = PageDataContextService.value.isLogged;

	const { enabled } = PageDataContextService.value.conf.enterpriseCloud;

	if (isLogged) return <GesCloudSignOut {...props} />;

	if (enabled) return null;

	return <GesCloudSignInMenu {...props} />;
};

const openGesCloudAppSettings = () =>
	ModalToOpenService.setValue<ComponentProps<typeof GesCloudAppSettingsEditor>>(ModalToOpen.GesAppSettings, {
		onClose: () => ModalToOpenService.resetValue(),
	});

const GesCloudSignOut = ({
	showHomeLayoutControls = false,
	showThemeToggle = false,
	triggerVariant = "default",
}: GesCloudSignInOutProps) => {
	const pageDataContext = PageDataContextService.value;
	const workspaceContext = pageDataContext.workspace;
	const currentWorkspaceName = pageDataContext.workspace.current;

	const workspaceConfig = workspaceContext.workspaces.find(
		(workspaceConfig) => workspaceConfig.path === currentWorkspaceName,
	);

	const onLogoutClick = useCallback(() => {
		const id = ModalToOpenService.addModal<ComponentProps<typeof SignOutGesCloud>>(ModalToOpen.GesCloudSignOut, {
			workspaceConfig,
			onClose: () => ModalToOpenService.removeModal(id),
		});
	}, [workspaceConfig]);

	const { organizations, isLoading } = useGesCloudOrganizationStore((state) => ({
		organizations: state.organizations,
		isLoading: state.isLoading,
	}));
	const currentOrganization = organizations?.find(
		(org) => org.type === "organization" && org.organizationInfo.current,
	)?.organizationInfo as OrganizationInfo;
	const canEditOrganization = !!currentOrganization?.canEdit;
	const organizationSettingsDisabled = isLoading || !currentOrganization || !canEditOrganization;

	const openOrganizationSettings = useCallback(() => {
		const id = ModalToOpenService.addModal(ModalToOpen.GesCloudOrganizationSettings, {
			onClose: () => ModalToOpenService.removeModal(id),
		});
	}, []);

	return (
		<UserAvatar
			homeLayoutControlsComponent={showHomeLayoutControls && <HomeLayoutControls />}
			logoutComponent={<SignOut />}
			onLogoutClick={onLogoutClick}
			onOrganizationSettingsClick={openOrganizationSettings}
			onSettingsClick={openGesCloudAppSettings}
			organizationSettingsComponent={<OpenOrganizationSettings />}
			organizationSettingsDisabled={organizationSettingsDisabled}
			organizationSettingsDisabledReason={
				!isLoading && currentOrganization && !canEditOrganization
					? t("enterprise-cloud.org-settings.cant-edit")
					: undefined
			}
			settingsComponent={<OpenSettings title={t("enterprise-cloud.app-settings.title")} />}
			themeToggleComponent={showThemeToggle && <ThemeMenuItem />}
			triggerVariant={triggerVariant}
		/>
	);
};

const GesCloudSignInMenu = ({
	showHomeLayoutControls = false,
	showThemeToggle = false,
	triggerVariant = "default",
}: GesCloudSignInOutProps) => {
	const { url: gesCloudUrl } = PageDataContextService.value.conf.enterpriseCloud;
	const [dropdownOpen, setDropdownOpen] = useState(false);

	const onOpenChange = async (open: boolean) => {
		if (open && !(await leaveEditModeGuard())) return;
		setDropdownOpen(open);
	};

	const openSignIn = () => {
		const modalId = ModalToOpenService.addModal(ModalToOpen.GesCloudSignIn, {
			gesCloudUrl,
			allowContinueWithoutAccount: false,
			onClose: () => ModalToOpenService.removeModal(modalId),
		});
	};

	return (
		<DropdownMenu onOpenChange={onOpenChange} open={dropdownOpen}>
			<Tooltip>
				<TooltipContent>
					<p>{t("account")}</p>
				</TooltipContent>
				<TooltipTrigger asChild>
					{triggerVariant === "glass" ? (
						<DropdownMenuTrigger asChild>
							<GlassToolbarToggleButton active={dropdownOpen} aria-label={t("account")} focusable>
								<Icon icon="user-round" />
							</GlassToolbarToggleButton>
						</DropdownMenuTrigger>
					) : (
						<DropdownMenuTriggerButton
							aria-label={t("account")}
							className="aspect-square p-0"
							variant="ghost"
						>
							<Icon className="h-5 w-5 shrink-0 stroke-[1.6]" icon="user-round" />
						</DropdownMenuTriggerButton>
					)}
				</TooltipTrigger>
			</Tooltip>
			<DropdownMenuContent align="end" className="min-w-[240px] rounded-xl">
				<DropdownMenuGroup>
					<DropdownMenuLabel className="text-sm font-semibold">{t("account")}</DropdownMenuLabel>
					<DropdownMenuSeparator />
					{showHomeLayoutControls && <HomeLayoutControls />}
					{showThemeToggle && <ThemeMenuItem />}
					<DropdownMenuItem className="rounded-lg" onSelect={() => openAppSettings(Level.app)}>
						<OpenSettings />
					</DropdownMenuItem>
					<DropdownMenuSeparator />
					<DropdownMenuItem className="rounded-lg" onSelect={openSignIn}>
						<Icon icon="log-in" />
						{t("sing-in")}
					</DropdownMenuItem>
				</DropdownMenuGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};
